import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Table,
  Badge,
  Spinner,
  Collapse,
  Input,
} from 'reactstrap';
import { displayValue as displayParcelValue } from '../../../../../../../../Components/QuantityTable/utils/quantityDeliveryUtils';
import {
  usesDyedUndyedAlpha,
  getAlphaColumnLabel,
  getProcurementProductNameId,
  getProcurementProductTypeId,
} from '../../../../../../../../shared/offer-domain/constants';
import { getAlphaImprovementFieldValue } from '../../../../../../../../shared/offer-domain/improvement';
import {
  fetchProductTypePricingUnit,
  formatIncotermRoute,
  formatParcelSize,
  formatPortDateRange,
  formatPortLocation,
  isAlphaFilled,
  coalesceAlphaPrice,
} from '../../../../../../utils/deliveryDetailUtils';
import { getAuthToken } from '../../../../../../../../auth/tokenvalidation';
import { api } from '../../../../../../../../config';
import useSubmittedBidder from '../../../../../../hooks/useSubmittedBidder';
import {
  buildRequirementsFromParcel,
  collectImprovementSelections,
  deliveryDetailRowMatchesBidder,
  displayValue,
  filterImprovementOfferRows,
  flattenEvaluationDeliveryDetail,
  flattenSubmittedBidderDeliveryDetails,
  getRequirementIncotermRouteLabel,
  isOfferRowImprovement,
  getOfferRowImprovementStatusLabel,
  isRowCapable,
  mergeDeliveryDetailPricingIntoRows,
  offerRowMatchesRequirement,
  isHpsAvailable,
} from '../../../../../../utils/tenderEvaluationHelpers';
import AlphaImprovementEditableCells from '../shared/AlphaImprovementEditableCells';
import {
  getHpsAlphaValues,
  resolveMatchingHpsKey,
  MatchingHpsBadge,
  HpsAlphaCell,
} from '../shared/hpsMatching';
import {
  enrichDeliveryItemsWithCounterOffers,
  enrichDeliveryItemsWithFobLocations,
  enrichItemsWithFobOriginParcel,
  formatFobLocationsJoined,
  getCapableOriginNamesFromFobLocations,
  isCounterDateChanged,
  isCounterQuantityChanged,
  isCounterValueChanged,
  isFobOriginDisplay,
  FobLocationLabel,
  renderSelectedOriginLocations,
} from '../../../../../../../../shared/offer-domain/delivery';
import {
  COUNTER_ACCENT,
  COUNTER_HIGHLIGHT_BG,
} from '../../../../../../../../shared/offer-domain/constants';
import ModalOffer from '../../ModalOffer';

const counterValueStyle = {
  backgroundColor: COUNTER_HIGHLIGHT_BG,
  color: COUNTER_ACCENT,
  fontWeight: 600,
};

const isFobIncotermRow = data =>
  String(data?.incoterm || '')
    .trim()
    .toUpperCase() === 'FOB';

const getRowOriginNames = data =>
  (Array.isArray(data?.fobLocationNames) ? data.fobLocationNames : []).filter(
    Boolean
  );

const resolveLoadingPortLabel = detailRow => {
  if (!detailRow) return '-';
  if (detailRow.fobOriginLocationName) {
    return detailRow.fobOriginLocationName;
  }
  if (detailRow.incotermLocationId) {
    return detailRow.loadingPort || '-';
  }
  const names = getRowOriginNames(detailRow);
  if (isFobOriginDisplay(detailRow.route, names)) {
    const capableNames = getCapableOriginNamesFromFobLocations(
      detailRow.incotermFOBLocations
    );
    const fallbackNames = (
      Array.isArray(detailRow.selectedCapableLocations)
        ? detailRow.selectedCapableLocations
        : []
    )
      .map(loc => loc?.name)
      .filter(Boolean);
    return renderSelectedOriginLocations(
      capableNames.length ? capableNames : fallbackNames
    );
  }
  if (isFobIncotermRow(detailRow) || names.length) {
    return formatFobLocationsJoined(names) || detailRow.loadingPort || '-';
  }
  return '-';
};

const PortSummary = ({
  label,
  port,
  parcelSize,
  dateRange,
  locationNames = null,
  route = null,
}) => {
  const hasLocationNames =
    Array.isArray(locationNames) && locationNames.length > 0;

  return (
    <Col md={2}>
      <div className='text-muted fw-semibold fs-12 mb-2'>{label}</div>
      <div className='fs-13'>
        <span className='text-muted d-block fs-12'>Port :</span>
        <span className='text-dark d-block mb-2'>
          {hasLocationNames ? (
            <FobLocationLabel
              names={locationNames}
              fallback={port}
              route={route}
            />
          ) : (
            displayValue(port)
          )}
        </span>
        <span className='text-muted d-block fs-12'>Parcel Size :</span>
        <span className='text-dark d-block mb-2'>
          {displayValue(parcelSize)}
        </span>
        {dateRange && (
          <>
            <span className='text-muted d-block fs-12'>Date :</span>
            <span className='text-dark d-block'>{dateRange}</span>
          </>
        )}
      </div>
    </Col>
  );
};

const renderHighlightedText = (value, highlighted) => (
  <span
    className={highlighted ? 'fw-semibold' : 'text-dark'}
    style={
      highlighted
        ? {
            ...counterValueStyle,
            padding: '0 4px',
            borderRadius: 2,
            display: 'inline-block',
          }
        : undefined
    }
  >
    {value}
  </span>
);

const renderPortBlock = (
  port,
  parcelSize,
  {
    startDate,
    endDate,
    fallbackDate,
    highlight = {},
    locationNames = null,
    route = null,
  } = {}
) => {
  const dateText = formatPortDateRange(startDate, endDate, fallbackDate);
  const highlightPort = highlight === true || highlight?.port;
  const highlightParcel = highlight === true || highlight?.parcelSize;
  const highlightDate = highlight === true || highlight?.date;
  const hasLocationNames =
    Array.isArray(locationNames) && locationNames.length > 0;

  return (
    <div className='fs-12 lh-base d-flex flex-column gap-1'>
      <span>
        <span className='text-muted'>Port : </span>
        {hasLocationNames ? (
          <span
            className={highlightPort ? 'fw-semibold' : 'text-dark'}
            style={highlightPort ? counterValueStyle : undefined}
          >
            <FobLocationLabel
              names={locationNames}
              fallback={port}
              route={route}
            />
          </span>
        ) : (
          renderHighlightedText(formatPortLocation(port), highlightPort)
        )}
      </span>
      <span>
        <span className='text-muted'>Parcel Size : </span>
        {renderHighlightedText(
          formatParcelSize(parcelSize, 'MB'),
          highlightParcel
        )}
      </span>
      <span>
        <span className='text-muted'>ALD/ADD : </span>
        {renderHighlightedText(dateText, highlightDate)}
      </span>
    </div>
  );
};

const splitAlphaPrice = (value, suffix = 'USD/BBL') => {
  if (!hasFilledValue(value)) return null;
  const text = String(value).trim();
  const match = text.match(/^(.*?)\s+(USD\/(?:BBL|MT))$/i);
  const amount = (match?.[1] || text).trim();
  const unit = match?.[2] || suffix;
  if (!amount) return null;
  return { amount, unit };
};

const AlphaPriceText = ({ value, suffix = 'USD/BBL' }) => {
  const parsed = splitAlphaPrice(value, suffix);
  if (!parsed) return '-';
  return (
    <span className='d-inline-flex flex-column lh-sm'>
      <span>{parsed.amount}</span>
      <span className='text-muted fw-normal fs-11'>{parsed.unit}</span>
    </span>
  );
};

const formatTimelineRange = timeline => {
  if (!timeline) return null;
  const start = timeline.startDate || timeline.startDate2;
  const end = timeline.endDate || timeline.endDate2;
  if (!start && !end) return null;
  if (start && end) return `${start} - ${end}`;
  return start || end;
};

const getProductLabel = procurement => {
  const productName =
    procurement?.productType?.product?.name ??
    procurement?.productType?.Product?.Name;
  const productDesc =
    procurement?.productType?.description ??
    procurement?.productType?.Description;

  if (productName && productDesc) return `${productName} - ${productDesc}`;
  return productName || productDesc || '-';
};

const getQuantityLabel = parcel => {
  const quantities =
    parcel?.quantities ?? parcel?.listQuantityInformation ?? [];
  if (!quantities.length) return '-';
  return quantities
    .map(item => `${displayParcelValue(item.quantity)} MB`)
    .join(', ');
};

const getUsesAlphaField = procurement =>
  !usesDyedUndyedAlpha(getProcurementProductNameId(procurement));

const findDetailRowsForBidder = (
  bidder,
  requirement,
  requirementIndex,
  parcelId,
  deliveryDetails
) => {
  const bidderRows = (deliveryDetails ?? []).filter(row =>
    deliveryDetailRowMatchesBidder(row, bidder)
  );

  const requirementLocationId = requirement?.incotermLocationId
    ? String(requirement.incotermLocationId)
    : '';

  const matchLocation = row => {
    if (!requirementLocationId) return true;
    return String(row?.incotermLocationId ?? '') === requirementLocationId;
  };

  const byIndex = bidderRows.filter(
    row => row.requirementIndex === requirementIndex && matchLocation(row)
  );
  if (byIndex.length) return byIndex;

  const byRequirement = bidderRows.filter(
    row =>
      offerRowMatchesRequirement(row, requirement, parcelId) &&
      matchLocation(row)
  );
  if (byRequirement.length) return byRequirement;

  if (
    bidderRows[requirementIndex] &&
    matchLocation(bidderRows[requirementIndex])
  ) {
    return [bidderRows[requirementIndex]];
  }

  const parcelRows = (deliveryDetails ?? []).filter(
    row => !parcelId || String(row.quantityDeliveryTermId) === String(parcelId)
  );

  const uniqueParentIds = new Set(
    parcelRows
      .map(row => row.parentId ?? row.procurementMitraId)
      .filter(Boolean)
      .map(String)
  );
  const canUseIndexFallback = uniqueParentIds.size <= 1;

  if (canUseIndexFallback) {
    const fallback = parcelRows.filter(
      row =>
        (row.requirementIndex === requirementIndex ||
          parcelRows[requirementIndex] === row) &&
        matchLocation(row)
    );
    if (fallback.length) return fallback;
    const single = parcelRows[requirementIndex];
    if (single && matchLocation(single)) return [single];
  }

  return [];
};

const hasFilledValue = value =>
  value != null && String(value).trim() !== '' && String(value).trim() !== '-';

const pickFirstFilled = (...values) => {
  for (const value of values) {
    if (hasFilledValue(value)) return value;
  }
  return null;
};

const requirementHasContent = requirement =>
  [
    requirement?.deliveryBasis,
    requirement?.incoterm,
    requirement?.route,
    requirement?.port1,
    requirement?.port2,
    requirement?.port3,
  ].some(hasFilledValue);

const requirementHasOfferData = ({
  requirement,
  requirementIndex,
  parcelId,
  deliveryDetails,
  bidders,
  procurement,
  improvementOnly = false,
}) => {
  const rows = getCompliantBidderRows({
    bidders,
    requirement,
    requirementIndex,
    parcelId,
    deliveryDetails,
    procurement,
  });
  return (improvementOnly ? filterImprovementOfferRows(rows) : rows).length > 0;
};

const hasCounterPricing = (row, usesAlphaField) =>
  !!(
    row?.quantityInformationDetailCounterId ||
    (usesAlphaField
      ? isAlphaFilled(row?.counterAlpha) || isAlphaFilled(row?.alpha2)
      : isAlphaFilled(row?.counterDyed) ||
        isAlphaFilled(row?.counterUndyed) ||
        isAlphaFilled(row?.dyed2) ||
        isAlphaFilled(row?.undyed2))
  );

const getOfferRowKey = (
  parcelId,
  requirementIndex,
  bidder,
  detailRow,
  kind = 'offer'
) =>
  `${parcelId ?? 'parcel'}-${requirementIndex}-${bidder?.id ?? bidder?.idOffer}-${detailRow?.quantityInformationDetailId ?? detailRow?.id ?? 'x'}-${detailRow?.incotermLocationId ?? 'noloc'}-${kind}`;

const getCompliantBidderRows = ({
  bidders,
  requirement,
  requirementIndex,
  parcelId,
  deliveryDetails,
  procurement,
}) => {
  if (!bidders?.length) return [];

  const usesAlphaField = getUsesAlphaField(procurement);

  return bidders.flatMap(bidder => {
    const detailRows = findDetailRowsForBidder(
      bidder,
      requirement,
      requirementIndex,
      parcelId,
      deliveryDetails
    );
    if (!detailRows.length) return [];

    return detailRows.flatMap(detailRow => {
      const rows = [];
      if (isRowCapable(detailRow, usesAlphaField)) {
        rows.push({
          bidder,
          detailRow,
          kind: 'offer',
          rowKey: getOfferRowKey(
            parcelId,
            requirementIndex,
            bidder,
            detailRow,
            'offer'
          ),
        });
      }
      if (hasCounterPricing(detailRow, usesAlphaField)) {
        rows.push({
          bidder,
          detailRow,
          kind: 'counter',
          rowKey: getOfferRowKey(
            parcelId,
            requirementIndex,
            bidder,
            detailRow,
            'counter'
          ),
        });
      }
      return rows;
    });
  });
};

const getFobOriginSelectionGroupKey = row => {
  const detailRow = row?.detailRow ?? {};
  if (!detailRow?.incotermLocationId) return null;
  return [
    row?.bidder?.id ?? row?.bidder?.idOffer ?? '',
    row?.kind ?? 'offer',
    detailRow.quantityInformationDetailId ?? detailRow.id ?? '',
    detailRow.quantityIncotermId ?? '',
  ].join('|');
};

const OfferDetailRows = ({
  compliantRows,
  isLoading,
  selectedRowKeys,
  onSelectRow,
  procurement,
  onViewDetail,
  improvementOnly = false,
  usesDyedUndyed = true,
  pricingUnit = null,
  operationalTolerance = null,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
  hpsAvailable = false,
}) => {
  const colSpan = improvementOnly
    ? usesDyedUndyed
      ? 13
      : 12
    : usesDyedUndyed
      ? 16
      : 15;

  if (isLoading) {
    return (
      <tr>
        <td colSpan={colSpan} className='text-center py-4'>
          <Spinner size='sm' color='primary' />
          <span className='text-muted ms-2 fs-13'>Loading offer detail...</span>
        </td>
      </tr>
    );
  }

  if (!compliantRows.length) {
    return (
      <tr>
        <td colSpan={colSpan} className='text-center text-muted py-4 fs-13'>
          {improvementOnly
            ? 'Belum ada data improvement.'
            : 'Belum ada data offer.'}
        </td>
      </tr>
    );
  }

  const usesAlphaField = getUsesAlphaField(procurement);
  const alphaSuffix = pricingUnit || 'USD/BBL';
  const fobOriginGroupMeta = {};
  compliantRows.forEach((row, index) => {
    const groupKey = getFobOriginSelectionGroupKey(row);
    if (!groupKey) return;
    if (!fobOriginGroupMeta[groupKey]) {
      fobOriginGroupMeta[groupKey] = {
        leadIndex: index,
        count: 0,
        keys: [],
      };
    }
    fobOriginGroupMeta[groupKey].count += 1;
    fobOriginGroupMeta[groupKey].keys.push(row.rowKey);
  });

  return compliantRows.map(({ bidder, detailRow, kind, rowKey }, index) => {
    const isCounter = kind === 'counter';
    const alphaDyedValue = usesAlphaField
      ? isCounter
        ? coalesceAlphaPrice(
            detailRow.counterAlpha,
            detailRow.alpha2,
            detailRow.alphaMitra2,
            detailRow.alpha,
            detailRow.alphaMitra1,
            detailRow.AlphaMitra1
          )
        : coalesceAlphaPrice(
            detailRow.alpha,
            detailRow.alphaMitra1,
            detailRow.AlphaMitra1,
            detailRow.alpha1
          )
      : isCounter
        ? coalesceAlphaPrice(
            detailRow.counterDyed,
            detailRow.dyed2,
            detailRow.dyed,
            detailRow.unitBbl
          )
        : coalesceAlphaPrice(detailRow.dyed, detailRow.unitBbl);
    const alphaUndyedValue = usesAlphaField
      ? null
      : isCounter
        ? coalesceAlphaPrice(
            detailRow.counterUndyed,
            detailRow.undyed2,
            detailRow.undyed,
            detailRow.cbm
          )
        : coalesceAlphaPrice(detailRow.undyed, detailRow.cbm);

    const counterDeliveryBasis =
      detailRow.counterDeliveryBasis ?? detailRow.counterParcelSize ?? null;
    const deliveryBasisChanged = isCounter
      ? isCounterQuantityChanged(
          detailRow.deliveryBasis ?? detailRow.parcelSize,
          counterDeliveryBasis
        )
      : false;

    const incoterm = isCounter
      ? (detailRow.counterIncoterm ?? detailRow.incoterm)
      : detailRow.incoterm;
    const route = isCounter
      ? (detailRow.counterRoute ?? detailRow.route)
      : detailRow.route;
    const originalIncotermRoute = formatIncotermRoute(
      detailRow.incoterm,
      detailRow.route
    );
    const counterIncotermRoute = formatIncotermRoute(incoterm, route);
    const incotermRouteChanged =
      isCounter &&
      !!detailRow.counterIncoterm &&
      isCounterValueChanged(originalIncotermRoute, counterIncotermRoute);

    const originalPort1 = detailRow.port1 || detailRow.loadingPort;
    const counterPort1 =
      detailRow.counterLoadingPort || detailRow.port1 || detailRow.loadingPort;

    const alphaDyedChanged =
      isCounter &&
      !usesAlphaField &&
      isCounterValueChanged(
        detailRow.dyed ?? detailRow.unitBbl,
        detailRow.counterDyed ?? detailRow.dyed2
      );
    const alphaUndyedChanged =
      isCounter &&
      !usesAlphaField &&
      isCounterValueChanged(
        detailRow.undyed ?? detailRow.cbm,
        detailRow.counterUndyed ?? detailRow.undyed2
      );
    const alphaChanged =
      isCounter &&
      usesAlphaField &&
      isCounterValueChanged(detailRow.alpha, detailRow.counterAlpha);
    const isImprovement = isOfferRowImprovement({ bidder, detailRow, kind });
    const fobGroupKey = getFobOriginSelectionGroupKey({
      bidder,
      detailRow,
      kind,
    });
    const fobGroup = fobGroupKey ? fobOriginGroupMeta[fobGroupKey] : null;
    const isFobGroupLead = !fobGroup || fobGroup.leadIndex === index;
    const fobGroupChecked = fobGroup
      ? fobGroup.keys.every(key => selectedRowKeys.has(key))
      : selectedRowKeys.has(rowKey);

    return (
      <tr
        key={rowKey ?? `${bidder.id ?? index}-${index}`}
        className='border-bottom'
      >
        {!improvementOnly && isFobGroupLead && (
          <td
            className='text-center align-middle'
            rowSpan={fobGroup?.count > 1 ? fobGroup.count : undefined}
          >
            <Input
              type='checkbox'
              className='form-check-input'
              checked={fobGroupChecked}
              disabled={isImprovement}
              onChange={e => {
                if (isImprovement) return;
                onSelectRow?.(rowKey, e.target.checked);
              }}
              aria-label={`Pilih ${displayValue(bidder.idOffer)}`}
            />
          </td>
        )}
        {!improvementOnly && (
          <td className='text-dark fs-12'>
            {getOfferRowImprovementStatusLabel({ bidder, detailRow, kind }) ===
            'Requested' ? (
              <Badge color='success' className='px-2 py-1 fs-11 fw-medium'>
                Requested
              </Badge>
            ) : (
              <Badge color='secondary' className='px-2 py-1 fs-11 fw-medium'>
                Not Requested
              </Badge>
            )}
          </td>
        )}
        <td className='text-dark'>
          <div>{displayValue(bidder.idOffer)}</div>
          <div className='text-muted fs-12'>
            {displayValue(bidder.companyIdentityMask)}
          </div>
        </td>
        <td>
          {isCounter ? (
            <span
              className='badge px-2 py-1 fs-11'
              style={{ backgroundColor: COUNTER_ACCENT, color: '#fff' }}
            >
              Counter
            </span>
          ) : (
            <Badge color='primary' className='px-2 py-1 fs-11'>
              Compliant
            </Badge>
          )}
        </td>
        <td
          className='text-dark'
          style={deliveryBasisChanged ? counterValueStyle : undefined}
        >
          {isCounter
            ? displayValue(
                counterDeliveryBasis != null && counterDeliveryBasis !== ''
                  ? formatParcelSize(counterDeliveryBasis, 'MB')
                  : null
              )
            : ''}
        </td>
        <td className='text-dark'>
          {isCounter
            ? operationalTolerance != null && operationalTolerance !== ''
              ? `${operationalTolerance}%`
              : '-'
            : ''}
        </td>
        <td
          className='text-dark'
          style={incotermRouteChanged ? counterValueStyle : undefined}
        >
          {isCounter ? counterIncotermRoute || '-' : ''}
        </td>
        <td className='text-dark fs-13 fw-medium'>
          {isCounter
            ? detailRow.counterLoadingPort ||
              resolveLoadingPortLabel(detailRow) ||
              '-'
            : resolveLoadingPortLabel(detailRow)}
        </td>
        <td>
          {isCounter
            ? renderPortBlock(
                counterPort1,
                detailRow.counterParcelSize ?? counterDeliveryBasis,
                {
                  startDate:
                    detailRow.counterStartDate ?? detailRow.port1StartDate,
                  endDate: detailRow.counterEndDate ?? detailRow.port1EndDate,
                  fallbackDate: detailRow.counterAddAld ?? detailRow.addAld,
                  locationNames: detailRow.fobLocationNames,
                  route: detailRow.counterRoute ?? detailRow.route,
                  highlight: {
                    port: isCounterValueChanged(
                      originalPort1,
                      detailRow.counterLoadingPort
                    ),
                    parcelSize: isCounterQuantityChanged(
                      detailRow.parcelSize ?? detailRow.deliveryBasis,
                      detailRow.counterParcelSize ?? counterDeliveryBasis
                    ),
                    date: isCounterDateChanged(
                      detailRow.port1StartDate,
                      detailRow.port1EndDate,
                      detailRow.counterStartDate,
                      detailRow.counterEndDate,
                      detailRow.addAld,
                      detailRow.counterAddAld
                    ),
                  },
                }
              )
            : ''}
        </td>
        <td>
          {isCounter && (detailRow.counterPort2 || detailRow.port2)
            ? renderPortBlock(
                detailRow.counterPort2 || detailRow.port2,
                detailRow.counterParcelSize2 ?? detailRow.parcelSize2,
                {
                  startDate:
                    detailRow.counterStartDate2 ?? detailRow.port2StartDate,
                  endDate: detailRow.counterEndDate2 ?? detailRow.port2EndDate,
                  fallbackDate: detailRow.counterAddAld2 ?? detailRow.addAld2,
                  highlight: {
                    port: isCounterValueChanged(
                      detailRow.port2,
                      detailRow.counterPort2
                    ),
                    parcelSize: isCounterQuantityChanged(
                      detailRow.parcelSize2,
                      detailRow.counterParcelSize2
                    ),
                    date: isCounterDateChanged(
                      detailRow.port2StartDate,
                      detailRow.port2EndDate,
                      detailRow.counterStartDate2,
                      detailRow.counterEndDate2,
                      detailRow.addAld2,
                      detailRow.counterAddAld2
                    ),
                  },
                }
              )
            : ''}
        </td>
        <td>
          {isCounter && (detailRow.counterPort3 || detailRow.port3)
            ? renderPortBlock(
                detailRow.counterPort3 || detailRow.port3,
                detailRow.counterParcelSize3 ?? detailRow.parcelSize3,
                {
                  startDate:
                    detailRow.counterStartDate3 ?? detailRow.port3StartDate,
                  endDate: detailRow.counterEndDate3 ?? detailRow.port3EndDate,
                  fallbackDate: detailRow.counterAddAld3 ?? detailRow.addAld3,
                  highlight: {
                    port: isCounterValueChanged(
                      detailRow.port3,
                      detailRow.counterPort3
                    ),
                    parcelSize: isCounterQuantityChanged(
                      detailRow.parcelSize3,
                      detailRow.counterParcelSize3
                    ),
                    date: isCounterDateChanged(
                      detailRow.port3StartDate,
                      detailRow.port3EndDate,
                      detailRow.counterStartDate3,
                      detailRow.counterEndDate3,
                      detailRow.addAld3,
                      detailRow.counterAddAld3
                    ),
                  },
                }
              )
            : ''}
        </td>
        <td
          className='text-dark'
          style={
            alphaChanged || alphaDyedChanged ? counterValueStyle : undefined
          }
        >
          <AlphaPriceText value={alphaDyedValue} suffix={alphaSuffix} />
        </td>
        {usesDyedUndyed && (
          <td
            className='text-dark'
            style={alphaUndyedChanged ? counterValueStyle : undefined}
          >
            <AlphaPriceText value={alphaUndyedValue} suffix={alphaSuffix} />
          </td>
        )}
        {!improvementOnly && (
          <>
            <td className='text-dark'>
              <HpsAlphaCell
                usesDyedUndyed={usesDyedUndyed}
                hps={getHpsAlphaValues(detailRow)}
                suffix={alphaSuffix}
                hpsAvailable={hpsAvailable}
              />
            </td>
            <td className='text-nowrap'>
              <MatchingHpsBadge
                matchKey={resolveMatchingHpsKey({
                  usesDyedUndyed,
                  offerAlpha: isCounter
                    ? detailRow.counterAlpha
                    : detailRow.alpha,
                  offerDyed: isCounter
                    ? (detailRow.counterDyed ?? detailRow.dyed2)
                    : (detailRow.dyed ?? detailRow.unitBbl),
                  offerUndyed: isCounter
                    ? (detailRow.counterUndyed ?? detailRow.undyed2)
                    : (detailRow.undyed ?? detailRow.cbm),
                  hps: getHpsAlphaValues(detailRow),
                  statusFromApi: pickFirstFilled(
                    detailRow.matchingHps,
                    detailRow.hpsMatching,
                    detailRow.matchingHpsStatus
                  ),
                  hpsAvailable,
                })}
              />
            </td>
          </>
        )}
        {improvementOnly ? (
          alphaImprovementEditable ? (
            <AlphaImprovementEditableCells
              detailRow={detailRow}
              kind={kind}
              usesDyedUndyed={usesDyedUndyed}
              pricingUnit={alphaSuffix}
              onAlphaImprovementChange={onAlphaImprovementChange}
            />
          ) : usesDyedUndyed ? (
            <>
              <td className='text-dark'>
                <AlphaPriceText
                  value={getAlphaImprovementFieldValue(detailRow, kind, 'dyed')}
                  suffix={alphaSuffix}
                />
              </td>
              <td className='text-dark'>
                <AlphaPriceText
                  value={getAlphaImprovementFieldValue(
                    detailRow,
                    kind,
                    'undyed'
                  )}
                  suffix={alphaSuffix}
                />
              </td>
            </>
          ) : (
            <td className='text-dark'>
              <AlphaPriceText
                value={getAlphaImprovementFieldValue(detailRow, kind, 'alpha')}
                suffix={alphaSuffix}
              />
            </td>
          )
        ) : (
          <td className='text-center'>
            <button
              type='button'
              className='btn btn-soft-primary btn-sm btn-icon'
              onClick={() => onViewDetail?.(bidder, detailRow)}
              title='Detail'
            >
              <i className='ri-eye-line align-bottom'></i>
            </button>
          </td>
        )}
      </tr>
    );
  });
};

const RequirementCard = ({
  requirement,
  requirementIndex,
  parcelId,
  bidders,
  isLoadingBidders,
  deliveryDetails,
  isLoadingDeliveryDetails,
  procurement,
  onViewDetail,
  improvementOnly = false,
  usesDyedUndyed = true,
  alphaColumnLabel = 'Alpha',
  pricingUnit = null,
  selectionKey,
  onRequirementSelectionChange,
  selectionClearToken = 0,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
}) => {
  const hasData = useMemo(
    () =>
      (!improvementOnly && requirementHasContent(requirement)) ||
      requirementHasOfferData({
        requirement,
        requirementIndex,
        parcelId,
        deliveryDetails,
        bidders,
        procurement,
        improvementOnly,
      }),
    [
      requirement,
      requirementIndex,
      parcelId,
      deliveryDetails,
      bidders,
      procurement,
      improvementOnly,
    ]
  );

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (hasData) setIsExpanded(true);
  }, [hasData]);

  const timeline = requirement?.timeline;
  const incotermRouteLabel = getRequirementIncotermRouteLabel(requirement);
  const selectAllRef = useRef(null);
  const operationalTolerance = procurement?.operationalTolerance;

  const requirementFobRow = useMemo(() => {
    const id = requirement?.quantityIncotermId;
    if (id === null || id === undefined || id === '') return null;
    return (
      deliveryDetails.find(
        row => String(row.quantityIncotermId ?? '') === String(id)
      ) ?? null
    );
  }, [deliveryDetails, requirement]);
  const summaryFobLocationNames = useMemo(() => {
    const id = requirement?.quantityIncotermId;
    if (id === null || id === undefined || id === '') {
      return requirementFobRow?.fobLocationNames ?? null;
    }
    const names = [];
    const seen = new Set();
    deliveryDetails.forEach(row => {
      if (String(row.quantityIncotermId ?? '') !== String(id)) return;
      const label =
        row.fobOriginLocationName ||
        (Array.isArray(row.selectedCapableLocations) &&
        row.selectedCapableLocations.length === 1
          ? row.selectedCapableLocations[0]?.name
          : null);
      if (label && !seen.has(label)) {
        seen.add(label);
        names.push(label);
      }
    });
    if (names.length) return names;
    return requirementFobRow?.fobLocationNames ?? null;
  }, [deliveryDetails, requirement, requirementFobRow]);
  const summaryFobRoute =
    requirementFobRow?.route ?? requirement?.route ?? null;

  const compliantRows = useMemo(() => {
    const rows = getCompliantBidderRows({
      bidders,
      requirement,
      requirementIndex,
      parcelId,
      deliveryDetails,
      procurement,
    });
    return improvementOnly ? filterImprovementOfferRows(rows) : rows;
  }, [
    bidders,
    requirement,
    requirementIndex,
    parcelId,
    deliveryDetails,
    procurement,
    improvementOnly,
  ]);

  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());

  const filteredRows = useMemo(() => {
    if (statusFilter === 'all') return compliantRows;
    if (statusFilter === 'counter') {
      return compliantRows.filter(row => row.kind === 'counter');
    }
    return compliantRows.filter(row => row.kind === 'offer');
  }, [compliantRows, statusFilter]);

  useEffect(() => {
    if (improvementOnly || !selectionClearToken) return;
    setSelectedRowKeys(new Set());
  }, [selectionClearToken, improvementOnly]);

  useEffect(() => {
    if (improvementOnly) return;
    const validKeys = new Set(compliantRows.map(row => row.rowKey));
    setSelectedRowKeys(prev => {
      const next = new Set([...prev].filter(key => validKeys.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [compliantRows, improvementOnly]);

  useEffect(() => {
    if (improvementOnly) return;
    const improvementKeys = compliantRows
      .filter(row => isOfferRowImprovement(row))
      .map(row => row.rowKey)
      .filter(Boolean);
    if (!improvementKeys.length) return;

    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      let changed = false;
      improvementKeys.forEach(key => {
        if (!next.has(key)) {
          next.add(key);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [compliantRows, improvementOnly]);

  useEffect(() => {
    if (improvementOnly || !selectionKey || !onRequirementSelectionChange)
      return;
    onRequirementSelectionChange(
      selectionKey,
      collectImprovementSelections({
        compliantRows,
        selectedKeys: [...selectedRowKeys],
        parcelId,
        requirementIndex,
        requirement,
        keyField: 'rowKey',
      })
    );
  }, [
    selectionKey,
    selectedRowKeys,
    compliantRows,
    parcelId,
    requirementIndex,
    requirement,
    onRequirementSelectionChange,
    improvementOnly,
  ]);

  const selectableRows = useMemo(
    () => filteredRows.filter(row => !isOfferRowImprovement(row)),
    [filteredRows]
  );

  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every(row => selectedRowKeys.has(row.rowKey));
  const someSelected = selectableRows.some(row =>
    selectedRowKeys.has(row.rowKey)
  );

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const handleSelectAll = checked => {
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      selectableRows.forEach(({ rowKey }) => {
        if (checked) next.add(rowKey);
        else next.delete(rowKey);
      });
      return next;
    });
  };

  const handleSelectRow = (rowKey, checked) => {
    const row = compliantRows.find(entry => entry.rowKey === rowKey);
    if (row && isOfferRowImprovement(row)) return;
    const groupKey = getFobOriginSelectionGroupKey(row);
    const keysToToggle = groupKey
      ? compliantRows
          .filter(
            entry =>
              getFobOriginSelectionGroupKey(entry) === groupKey &&
              !isOfferRowImprovement(entry)
          )
          .map(entry => entry.rowKey)
          .filter(Boolean)
      : [rowKey];

    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      keysToToggle.forEach(key => {
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  return (
    <Card className='border shadow-none mb-3'>
      <CardHeader
        className='bg-light border-bottom p-3 d-flex justify-content-between align-items-center cursor-pointer'
        onClick={() => setIsExpanded(prev => !prev)}
        role='button'
        aria-expanded={isExpanded}
        aria-controls={`requirement-body-${requirementIndex}`}
      >
        <div>
          <h6 className='mb-0 text-dark fw-bold fs-13'>
            Requirement {requirementIndex + 1}
          </h6>
          <span className='text-muted fs-12'>{incotermRouteLabel}</span>
        </div>
        <i
          className={`fs-18 ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}
        ></i>
      </CardHeader>

      <Collapse isOpen={isExpanded}>
        <CardBody id={`requirement-body-${requirementIndex}`} className='p-0'>
          <div className='p-3 border-bottom'>
            <Row className='g-3'>
              <Col md={2}>
                <div className='text-muted fw-semibold fs-12 mb-2'>
                  Delivery Basis
                </div>
                <div className='text-dark fs-13 mt-4'>
                  {displayValue(requirement.deliveryBasis)}
                </div>
              </Col>
              <Col md={2}>
                <div className='text-muted fw-semibold fs-12 mb-2'>
                  Incoterm - Route
                </div>
                <div className='text-dark fs-13 mt-4'>
                  {[requirement.incoterm, requirement.route]
                    .filter(Boolean)
                    .join(' - ') || '-'}
                </div>
              </Col>
              <Col md={2}>
                <div className='text-muted fw-semibold fs-12 mb-2'>
                  Operational Tolerances
                </div>
                <div className='text-dark fs-13 mt-4'>
                  {operationalTolerance != null && operationalTolerance !== ''
                    ? `${operationalTolerance}%`
                    : '-'}
                </div>
              </Col>
              <PortSummary
                label='Port 1'
                port={requirement.port1}
                parcelSize={requirement.dischargeQty1}
                dateRange={formatTimelineRange(timeline)}
                locationNames={summaryFobLocationNames}
                route={summaryFobRoute}
              />
              <PortSummary
                label='Port 2'
                port={requirement.port2}
                parcelSize={requirement.dischargeQty2}
              />
              <PortSummary
                label='Port 3'
                port={requirement.port3}
                parcelSize={requirement.dischargeQty3}
              />
            </Row>
          </div>

          <div className='table-responsive m-3 border rounded'>
            <Table className='table-borderless align-middle mb-0 custom-table-spacing'>
              <thead className='bg-light text-muted fs-12 border-bottom'>
                <tr>
                  {!improvementOnly && (
                    <th
                      scope='col'
                      style={{ width: '44px' }}
                      className='text-center'
                    >
                      <Input
                        innerRef={selectAllRef}
                        type='checkbox'
                        className='form-check-input'
                        checked={allSelected}
                        disabled={
                          isLoadingBidders ||
                          isLoadingDeliveryDetails ||
                          !selectableRows.length
                        }
                        onChange={e => handleSelectAll(e.target.checked)}
                        aria-label='Pilih semua offer'
                      />
                    </th>
                  )}
                  {!improvementOnly && (
                    <th scope='col' style={{ minWidth: '130px' }}>
                      Status Improvement
                    </th>
                  )}
                  <th scope='col'>ID Offer</th>
                  <th scope='col' style={{ minWidth: '130px' }}>
                    <div className='d-flex flex-column gap-1'>
                      <span>Status</span>
                      <Input
                        type='select'
                        bsSize='sm'
                        className='form-select form-select-sm py-0'
                        style={{ minHeight: '28px' }}
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        aria-label='Filter status'
                      >
                        <option value='all'>All</option>
                        <option value='compliant'>Compliant</option>
                        <option value='counter'>Counter</option>
                      </Input>
                    </div>
                  </th>
                  <th scope='col'>Delivery Basis</th>
                  <th scope='col'>Operational Tolerances</th>
                  <th scope='col'>Incoterm - Route</th>
                  <th scope='col' style={{ minWidth: '140px' }}>
                    Loading Port
                  </th>
                  <th scope='col' style={{ minWidth: '180px' }}>
                    Port 1
                  </th>
                  <th scope='col'>Port 2</th>
                  <th scope='col'>Port 3</th>
                  <th scope='col'>
                    {usesDyedUndyed ? 'Alpha Dyed' : alphaColumnLabel}
                  </th>
                  {usesDyedUndyed && <th scope='col'>Alpha Undyed</th>}
                  {!improvementOnly && (
                    <>
                      <th scope='col' style={{ minWidth: '120px' }}>
                        HPS Alpha
                      </th>
                      <th scope='col' style={{ minWidth: '170px' }}>
                        Matching HPS
                      </th>
                    </>
                  )}
                  {improvementOnly ? (
                    usesDyedUndyed ? (
                      <>
                        <th scope='col'>Alpha Dyed (Improvement)</th>
                        <th scope='col'>Alpha Undyed (Improvement)</th>
                      </>
                    ) : (
                      <th scope='col'>Alpha (Improvement)</th>
                    )
                  ) : (
                    <th scope='col'>Action</th>
                  )}
                </tr>
              </thead>
              <tbody className='fs-13'>
                <OfferDetailRows
                  compliantRows={filteredRows}
                  isLoading={isLoadingBidders || isLoadingDeliveryDetails}
                  selectedRowKeys={selectedRowKeys}
                  onSelectRow={handleSelectRow}
                  procurement={procurement}
                  onViewDetail={onViewDetail}
                  improvementOnly={improvementOnly}
                  usesDyedUndyed={usesDyedUndyed}
                  pricingUnit={pricingUnit}
                  operationalTolerance={operationalTolerance}
                  alphaImprovementEditable={alphaImprovementEditable}
                  onAlphaImprovementChange={onAlphaImprovementChange}
                  hpsAvailable={isHpsAvailable(procurement)}
                />
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Collapse>
    </Card>
  );
};

const ParcelOfferCard = ({
  parcel,
  procurement,
  bidders,
  isLoadingBidders,
  deliveryDetails,
  isLoadingDeliveryDetails,
  onViewDetail,
  improvementOnly = false,
  usesDyedUndyed = true,
  alphaColumnLabel = 'Alpha',
  pricingUnit = null,
  onRequirementSelectionChange,
  selectionClearToken = 0,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
}) => {
  const requirements = useMemo(
    () => buildRequirementsFromParcel(parcel),
    [parcel]
  );
  const parcelId = parcel?.id ?? parcel?.quantityDeliveryTermId ?? null;
  const parcelDeliveryDetails = useMemo(
    () =>
      parcelId
        ? deliveryDetails.filter(
            row => String(row.quantityDeliveryTermId) === String(parcelId)
          )
        : deliveryDetails,
    [deliveryDetails, parcelId]
  );
  const operationalTolerance = procurement?.operationalTolerance;
  const headerTolerance = operationalTolerance;

  return (
    <Card className='border shadow-none mb-4'>
      <CardHeader className='bg-light border-bottom p-3'>
        <h6 className='mb-0 text-dark fw-bold fs-14'>
          Delivery Unit : {displayValue(parcel?.deliveryUnit, '-')}
        </h6>
      </CardHeader>

      <CardBody className='p-4'>
        <Row className='mb-4'>
          <Col md={4} className='border-end'>
            <div className='text-dark fw-semibold fs-13 mb-1'>Product Name</div>
            <div className='text-muted fs-13'>
              {getProductLabel(procurement)}
            </div>
          </Col>
          <Col md={4} className='border-end'>
            <div className='text-dark fw-semibold fs-13 mb-1'>Quantity</div>
            <div className='text-muted fs-13'>{getQuantityLabel(parcel)}</div>
          </Col>
          <Col md={4}>
            <div className='text-dark fw-semibold fs-13 mb-1'>
              Operation Tolerance
            </div>
            <div className='text-muted fs-13'>
              {headerTolerance != null && headerTolerance !== ''
                ? `${headerTolerance}%`
                : '-'}
            </div>
          </Col>
        </Row>

        {requirements.map((requirement, index) => (
          <RequirementCard
            key={`${parcel?.id || 'parcel'}-req-${index}`}
            selectionKey={`${parcelId || 'parcel'}-req-${index}`}
            requirement={requirement}
            requirementIndex={index}
            bidders={bidders}
            isLoadingBidders={isLoadingBidders}
            parcelId={parcelId}
            deliveryDetails={parcelDeliveryDetails}
            isLoadingDeliveryDetails={isLoadingDeliveryDetails}
            procurement={procurement}
            onViewDetail={onViewDetail}
            improvementOnly={improvementOnly}
            usesDyedUndyed={usesDyedUndyed}
            alphaColumnLabel={alphaColumnLabel}
            pricingUnit={pricingUnit}
            onRequirementSelectionChange={onRequirementSelectionChange}
            selectionClearToken={selectionClearToken}
            alphaImprovementEditable={alphaImprovementEditable}
            onAlphaImprovementChange={onAlphaImprovementChange}
          />
        ))}
      </CardBody>
    </Card>
  );
};

const OfferTableNonLpg = ({
  procurementId,
  procurement,
  parcels = [],
  isLoadingParcels = false,
  onSelectedOffersChange,
  selectionClearToken = 0,
  viewMode = 'offer',
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
  onImprovementMeta,
  isActive = true,
}) => {
  const improvementOnly = viewMode === 'improvement';
  const { items: bidders, isLoading: isLoadingBidders } = useSubmittedBidder(
    procurementId,
    { isActive, refreshToken: selectionClearToken }
  );

  const baseDeliveryDetails = useMemo(
    () => flattenSubmittedBidderDeliveryDetails(bidders),
    [bidders]
  );

  const [deliveryDetails, setDeliveryDetails] = useState([]);
  const [isEnriching, setIsEnriching] = useState(false);
  const [hasEnrichmentSettled, setHasEnrichmentSettled] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [pricingUnit, setPricingUnit] = useState(null);
  const [selectionByRequirement, setSelectionByRequirement] = useState({});

  const handleRequirementSelectionChange = useCallback(
    (selectionKey, entries) => {
      setSelectionByRequirement(prev => {
        const prevEntries = prev[selectionKey] || [];
        if (
          prevEntries.length === entries.length &&
          prevEntries.every(
            (entry, index) =>
              entry.quantityIncotermId === entries[index]?.quantityIncotermId &&
              entry.kind === entries[index]?.kind
          )
        ) {
          return prev;
        }
        return { ...prev, [selectionKey]: entries };
      });
    },
    []
  );

  useEffect(() => {
    if (improvementOnly || !onSelectedOffersChange) return;
    onSelectedOffersChange?.(
      Object.values(selectionByRequirement).flatMap(entries => entries || [])
    );
  }, [selectionByRequirement, onSelectedOffersChange, improvementOnly]);

  useEffect(() => {
    if (improvementOnly || !selectionClearToken) return;
    setSelectionByRequirement({});
  }, [selectionClearToken, improvementOnly]);

  const usesDyedUndyed = usesDyedUndyedAlpha(
    getProcurementProductNameId(procurement)
  );
  const alphaColumnLabel = getAlphaColumnLabel(pricingUnit);

  useEffect(() => {
    let cancelled = false;

    const enrichDetails = async () => {
      setHasEnrichmentSettled(false);

      if (!baseDeliveryDetails.length) {
        if (!cancelled) {
          setDeliveryDetails([]);
          setIsEnriching(false);
          setHasEnrichmentSettled(true);
        }
        return;
      }

      const token = getAuthToken();
      if (!token || !procurementId) {
        try {
          const expanded = await enrichItemsWithFobOriginParcel({
            items: baseDeliveryDetails,
          });
          if (!cancelled) setDeliveryDetails(expanded);
        } catch (error) {
          if (!cancelled) setDeliveryDetails(baseDeliveryDetails);
        } finally {
          if (!cancelled) {
            setIsEnriching(false);
            setHasEnrichmentSettled(true);
          }
        }
        return;
      }

      setIsEnriching(true);
      try {
        const byTermId = new Map();
        baseDeliveryDetails.forEach(row => {
          const termId = row.quantityDeliveryTermId ?? 'default';
          if (!byTermId.has(termId)) byTermId.set(termId, []);
          byTermId.get(termId).push(row);
        });

        const enriched = [];
        for (const [termId, items] of byTermId.entries()) {
          let nextItems = items;
          if (termId !== 'default') {
            try {
              const detailResponse = await fetch(
                `${api.API_URL}/procurement/get-delivery-detail?PageNumber=1&PageSize=500&procurementId=${encodeURIComponent(procurementId)}&quantityDeliveryTermId=${encodeURIComponent(termId)}`,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  credentials: 'same-origin',
                }
              );
              if (detailResponse.ok) {
                const detailResult = await detailResponse.json();
                const bidderMap = Object.fromEntries(
                  (bidders || []).map(bidder => [String(bidder.id), bidder])
                );
                const detailRows = flattenEvaluationDeliveryDetail(
                  detailResult,
                  bidderMap,
                  termId
                );
                nextItems = mergeDeliveryDetailPricingIntoRows(
                  nextItems,
                  detailRows
                );
              }
            } catch (error) {
              // keep submitted-bidder rows when delivery-detail merge fails
            }
            try {
              nextItems = await enrichDeliveryItemsWithFobLocations({
                quantityDeliveryTermId: termId,
                token,
                items: nextItems,
              });
            } catch (error) {
              // keep base items when FOB enrichment fails
            }
            try {
              nextItems = await enrichDeliveryItemsWithCounterOffers({
                quantityDeliveryTermId: termId,
                token,
                items: nextItems,
                requireIncotermCounter: false,
              });
            } catch (error) {
              // keep base items when counter enrichment fails
            }
          }
          try {
            nextItems = await enrichItemsWithFobOriginParcel({
              procurementId,
              token,
              items: nextItems,
            });
          } catch (error) {
            // keep base items when FOB Origin parcel expansion fails
          }
          enriched.push(...nextItems);
        }

        if (!cancelled) setDeliveryDetails(enriched);
      } finally {
        if (!cancelled) {
          setIsEnriching(false);
          setHasEnrichmentSettled(true);
        }
      }
    };

    enrichDetails();
    return () => {
      cancelled = true;
    };
  }, [baseDeliveryDetails, procurementId, selectionClearToken, bidders]);

  useEffect(() => {
    if (!improvementOnly || !onImprovementMeta) return;
    if (!hasEnrichmentSettled || isEnriching) return;
    onImprovementMeta({ deliveryDetails, ready: true });
  }, [
    deliveryDetails,
    improvementOnly,
    onImprovementMeta,
    hasEnrichmentSettled,
    isEnriching,
  ]);

  useEffect(() => {
    if (usesDyedUndyed) {
      setPricingUnit(null);
      return;
    }
    const productTypeId = getProcurementProductTypeId(procurement);
    const token = getAuthToken();
    let cancelled = false;
    fetchProductTypePricingUnit(productTypeId, token).then(unit => {
      if (!cancelled) setPricingUnit(unit);
    });
    return () => {
      cancelled = true;
    };
  }, [usesDyedUndyed, procurement]);

  const handleViewOfferDetail = (bidder, detailRow) => {
    if (!bidder?.id) return;
    setSelectedOffer({ bidder, detailRow });
    setIsDetailModalOpen(true);
  };

  const toggleDetailModal = () => {
    setIsDetailModalOpen(prev => !prev);
    if (isDetailModalOpen) {
      setSelectedOffer(null);
    }
  };

  const hasImprovementData = useMemo(() => {
    if (!improvementOnly) return true;
    const parcelItems = parcels.length
      ? parcels
      : [{ id: 'default', deliveryUnit: '-' }];
    return parcelItems.some(parcel => {
      const parcelId = parcel?.id ?? parcel?.quantityDeliveryTermId ?? null;
      const parcelDeliveryDetails = parcelId
        ? deliveryDetails.filter(
            row => String(row.quantityDeliveryTermId) === String(parcelId)
          )
        : deliveryDetails;
      return buildRequirementsFromParcel(parcel).some((requirement, index) =>
        requirementHasOfferData({
          requirement,
          requirementIndex: index,
          parcelId,
          deliveryDetails: parcelDeliveryDetails,
          bidders,
          procurement,
          improvementOnly: true,
        })
      );
    });
  }, [improvementOnly, parcels, deliveryDetails, bidders, procurement]);

  if (isLoadingParcels || isLoadingBidders) {
    return (
      <div className='text-center py-4'>
        <Spinner size='sm' color='primary' />
        <p className='text-muted mt-2 mb-0 fs-13'>Loading offer data...</p>
      </div>
    );
  }

  if (improvementOnly && (!hasEnrichmentSettled || isEnriching)) {
    return (
      <div className='text-center py-4'>
        <Spinner size='sm' color='primary' />
        <p className='text-muted mt-2 mb-0 fs-13'>
          Loading improvement data...
        </p>
      </div>
    );
  }

  if (!bidders.length) {
    return (
      <div className='text-center text-muted py-4 fs-13'>
        {improvementOnly
          ? 'Belum ada data improvement.'
          : 'Belum ada penawaran yang disubmit.'}
      </div>
    );
  }

  if (improvementOnly && !hasImprovementData) {
    return (
      <div className='text-center text-muted py-4 fs-13'>
        Belum ada data improvement.
      </div>
    );
  }

  const parcelItems = parcels.length
    ? parcels
    : [{ id: 'default', deliveryUnit: '-' }];

  return (
    <>
      {parcelItems.map(parcel => (
        <ParcelOfferCard
          key={parcel.id || parcel.deliveryUnit}
          parcel={parcel}
          procurement={procurement}
          bidders={bidders}
          isLoadingBidders={isLoadingBidders}
          deliveryDetails={deliveryDetails}
          isLoadingDeliveryDetails={isEnriching}
          onViewDetail={handleViewOfferDetail}
          improvementOnly={improvementOnly}
          usesDyedUndyed={usesDyedUndyed}
          alphaColumnLabel={alphaColumnLabel}
          pricingUnit={pricingUnit}
          onRequirementSelectionChange={
            improvementOnly ? undefined : handleRequirementSelectionChange
          }
          selectionClearToken={improvementOnly ? 0 : selectionClearToken}
          alphaImprovementEditable={alphaImprovementEditable}
          onAlphaImprovementChange={onAlphaImprovementChange}
        />
      ))}
      {!improvementOnly && (
        <ModalOffer
          isOpen={isDetailModalOpen}
          toggle={toggleDetailModal}
          bidder={selectedOffer?.bidder}
          procurement={procurement}
        />
      )}
    </>
  );
};

export default OfferTableNonLpg;
